/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
 * filterregel-personeel-edit.component.ts
 * Gegenereerd met Junie (AI), 07-10-2025
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Editorcomponent voor personeel-regels met multiselect op Personeel.
 */

import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {FilterregelEditComponent} from './filterregel-edit.component';
import {FiltertoolPlanitempersoneelRegel} from '../../../services/filtertool/regels/filtertool-personeelregel';
import {FiltertoolRegelTypeNamen} from '../../../services/filtertool/filtertool-regels';
import {Personeel} from '../../../models/personeel';
import {FilterregelActiesComponent} from './filterregel-acties.component';

/**
 * Editor voor personeelregels: selecteer 1 of meer medewerkers (multiselect) en beheer verwijderen.
 */
@Component({
    standalone: true,
    selector: 'filterregel-personeel-edit',
    imports: [CommonModule, FormsModule, NgSelectModule, FilterregelActiesComponent],
    templateUrl: './filterregel-personeel-edit.component.html',
    styleUrls: ['./filterregel-personeel-edit.component.scss', './filterregel-editors.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterregelPersoneelEditComponent extends FilterregelEditComponent<FiltertoolPlanitempersoneelRegel> implements OnInit {
    // DI-services

    // Inputs (properties)
    @Input({required: true}) override regel!: FiltertoolPlanitempersoneelRegel;

    // Properties
    /** Mapping met label/omschrijving per regeltype voor weergave. */
    public readonly regelTypeNamen = FiltertoolRegelTypeNamen;
    /** Alle beschikbare medewerkers (lookup, alleen id en naam benodigd). */
    public personeel: Personeel[] = [];
    /** Lokale binding van gekozen ids voor ngModel. */
    public gekozenPersoneelIds: number[] = [];

    constructor() {
        super('debug');
    }

    // Lifecycle hooks
    async ngOnInit(): Promise<void> {
        // init selectie
        this.gekozenPersoneelIds = Array.isArray(this.regel?.personeelIds) ? [...this.regel.personeelIds] : [];
        // haal personeel op: alle personen, alleen id en naam (naam is getter, dus velden voor/achternaam volstaan)
        this.personeel = await Personeel.getAll([], [[Personeel, { attributes: ['id', 'voornaam', 'tussenvoegsels', 'achternaam'] }]]);
        // trigger hertekenen omdat OnPush + @if anders niet update na async load
        this.cd.markForCheck();
        this.log.debug('Personeel geladen', this.personeel?.length);
    }

    // Public methods
    /**
     * Slaat gewijzigde selectie op in de regel en emit de wijziging.
     */
    public personeelGewijzigd(): void {
        const clean = (this.gekozenPersoneelIds || []).filter(v => v != null);
        if (this.regel) {
            this.regel.personeelIds = [...clean];
            this.wijzig.emit(this.regel);
        }
        try {
            this.cd.detectChanges();
        } catch {
            this.cd.markForCheck();
        }
    }
}
